import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon (Leaflet bundler quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATE_CENTERS = {
  AC: [-9.02, -70.81, 7], AL: [-9.57, -36.78, 8], AM: [-3.42, -63.58, 6],
  AP: [1.41, -51.77, 7], BA: [-12.97, -41.39, 6], CE: [-5.09, -39.24, 7],
  DF: [-15.79, -47.88, 10], ES: [-19.55, -40.68, 7], GO: [-16.32, -49.06, 6],
  MA: [-5.09, -45.30, 6], MG: [-18.10, -44.38, 6], MS: [-20.44, -54.68, 6],
  MT: [-12.68, -55.44, 6], PA: [-3.79, -52.87, 6], PB: [-7.24, -36.78, 7],
  PE: [-8.28, -37.06, 7], PI: [-7.71, -42.84, 6], PR: [-24.62, -51.65, 6],
  RJ: [-22.24, -42.53, 7], RN: [-5.79, -36.44, 7], RO: [-11.05, -63.15, 7],
  RR: [2.16, -61.66, 6], RS: [-30.12, -53.24, 6], SC: [-27.24, -50.32, 7],
  SE: [-10.68, -37.28, 8], SP: [-22.19, -48.79, 6], TO: [-10.25, -48.34, 6],
};

/**
 * PinPickerMap — Leaflet map that lets the admin drop/drag a single pin.
 * Emits { lat, lng } via onChange whenever the marker moves.
 *
 * Props:
 *   defaultState: UF (ex: "DF") — used for initial view when no pin yet
 *   value: { lat, lng } | null
 *   onChange: ({lat, lng}) => void
 *   height: CSS height (default 380)
 */
export const PinPickerMap = ({ defaultState = 'DF', value = null, onChange, height = 380 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const center = value
      ? [value.lat, value.lng, 14]
      : STATE_CENTERS[defaultState] || [-14.235, -51.9253, 4];
    const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: true })
      .setView([center[0], center[1]], center[2]);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);

    if (value) {
      markerRef.current = L.marker([value.lat, value.lng], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', (e) => {
        const { lat, lng } = e.target.getLatLng();
        onChange && onChange({ lat, lng });
      });
    }

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
        markerRef.current.on('dragend', (ev) => {
          const p = ev.target.getLatLng();
          onChange && onChange({ lat: p.lat, lng: p.lng });
        });
      }
      onChange && onChange({ lat, lng });
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // If value changes externally (e.g. reset), keep marker in sync
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (value && markerRef.current) {
      markerRef.current.setLatLng([value.lat, value.lng]);
    } else if (value && !markerRef.current) {
      markerRef.current = L.marker([value.lat, value.lng], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', (e) => {
        const p = e.target.getLatLng();
        onChange && onChange({ lat: p.lat, lng: p.lng });
      });
    }
  }, [value, onChange]);

  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        className="w-full rounded-xl overflow-hidden border border-slate-200"
        style={{ height }}
        data-testid="pin-picker-map"
      />
      <p className="text-[11px] text-slate-500">
        Clique no mapa para posicionar o pin ou arraste o marcador. A localização será atribuída em massa
        a todos os apoiadores importados (o sistema identifica cidade e bairro automaticamente).
      </p>
    </div>
  );
};
